import React from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../elements/Icon/Icon';
import {colorTheme} from '../../mixin/colorTheme';
import {FormIconWrapper} from '../form-control-features/FormIconWrapper';
import {FormSelectArrowIcon} from '../form-control-features/FormSelectArrowIcon';
import {FormLabel} from '../form-labels/FormLabel';
import {LabeledPrefix, LabeledSuffix} from '../form-labels/FormLabeledSuffixPrefix';
import {formFocus, formValidationError} from '../form-state/formState';
import {FormControl} from './FormControl';

// FormContainer is the main container for all Form control types.
// It provides the background, border, and colors.
// It also provides the necessary props for the form controls to be used: (labels, prefix, suffix, and validation error message.)
export const FormContainer = ({
    children,
    errorMessage,
    disabled,
    label,
    name,
    prefix,
    size,
    suffix,
    statusBadge,
    code,
    // === Internal properties === ///
    formSelect,
}) => {
    return (
        //  ---- FormGroup Structure ----- //
        <FormGroup size={size}>
            <FormWrapper>
                {(label || statusBadge) && (
                    <FormLabel htmlFor={name}>
                        <span>{label}</span>
                        {statusBadge && <FormIconWrapper>{statusBadge}</FormIconWrapper>}
                    </FormLabel>
                )}
                {/* FormControlBox Structure (will include prefix and suffix if called out) */}
                <Wrapper disabled={disabled}>
                    {prefix && <LabeledPrefix htmlFor={name}>{prefix}</LabeledPrefix>}
                    <FormControl code={code}>
                        {children}
                        {formSelect && (
                            <FormSelectArrowIcon>
                                <Icon color="var(--default-text-color)" name="edit" />
                            </FormSelectArrowIcon>
                        )}
                    </FormControl>

                    {suffix && <LabeledSuffix htmlFor={name}>{suffix}</LabeledSuffix>}
                </Wrapper>
            </FormWrapper>

            {/* // --- Validation error message --- // */}
            {errorMessage && <>{errorMessage}</>}
        </FormGroup>
    );
};

/**
 * @type {import('styled-components').StyledComponent<'div', any, {size?: 'sm'}, never>}
 */
export const FormGroup = styled.div`
    ${colorTheme({
        /* Importing color theme */
        '--form-box-error-background-color': {dark: 'var(--red-07)', light: 'var(--danger-light)'},
        '--form-labeled-affix-error-background-color': {dark: 'var(--red-08)', light: 'var(--red-05)'},
    })};

    --form-animation-duration: 0.6s;

    --form-box-background-color: var(--container-background-color-100);
    --form-box-hover-background-color: var(--primary);

    --form-box-background-focus-color: var(--container-background-hover-color-100);
    --form-box-background-transparent-color: var(--container-background-trans);
    --form-box-border-color: var(--container-border-color-100);
    --form-box-border-focus-color: var(--container-border-hover-color-100);

    --form-labeled-affix-background-color: var(--container-background-color-300);
    --form-labeled-affix-background-focus-color: var(--container-background-hover-color-500);
    --form-labeled-affix-border-color: var(--container-border-color-100);

    // === Form control dimensions === //
    --textarea-height-form-control: 120px;
    --height-form-control: 32px;
    --height-form-control-sm: 22px;
    --height-form-control-mz: 30px;
    --padding-form-control: var(--space-sm);
    --padding-form-control-mz: 0 var(--space-sm);
    --radius-form-control: 6px;

    display: block;
    font-size: var(--text-size-default);
    font-weight: 600;
    margin-bottom: var(--space-md);
    text-align: left;
    width: 100%;
    ${formValidationError}
    ${({size}) => {
        if (size === 'sm')
            return css`
                --height-form-control: var(--height-form-control-sm);
                * {
                    font-size: 12px;
                }
            `;
    }}
`;

/**
 * @type {import('styled-components').StyledComponent<'div', any, {disabled?: boolean}, never>}
 */
export const Wrapper = styled.div`
    // ---------------------------------- //
    // === Prefix & Suffix Flex Setup === //
    // ---------------------------------- //
    &:has(${LabeledPrefix.toString()}:nth-child(1) + ${FormControl.toString()}:nth-child(2)),
    &:has(${FormControl.toString()}:nth-child(1) + ${LabeledSuffix.toString()}:nth-child(2)),
    &:has(${LabeledPrefix.toString()}:nth-child(1) + ${FormControl.toString()}:nth-child(2))
        + ${LabeledSuffix.toString()}:nth-child(3) {
        align-items: center;
        display: flex;

        ${LabeledPrefix},
        ${LabeledSuffix} {
            flex-shrink: 0;
        }
    }

    // ------------------------- //
    // === Prefix only Setup === //
    // ------------------------- //
    &:has(${LabeledPrefix.toString()}:nth-child(1) + ${FormControl.toString()}:nth-child(2)) {
        ${FormControl} {
            border-radius: 0 var(--radius-form-control) var(--radius-form-control) 0;
        }
    }

    // ------------------------- //
    // === Suffix only Setup === //
    // ------------------------- //
    &:has(${FormControl.toString()}:nth-child(1) + ${LabeledSuffix.toString()}:nth-child(2)) {
        ${FormControl} {
            border-radius: var(--radius-form-control) 0 0 var(--radius-form-control);
        }
    }

    // ------------------------------------ //
    // === Suffix for Date picker Setup === //
    // ------------------------------------ //
    &:has(.form-datepicker-input) {
        position: relative;
        cursor: pointer;
        ${FormControl} {
            padding: 0 !important;
            border-radius: var(--radius-form-control) !important;
        }
        ${LabeledSuffix} {
            position: absolute;
            top: 0;
            right: 0;
            pointer-events: none;
            border: 1px solid var(--form-labeled-affix-border-color);
        }
        .react-datepicker-wrapper {
            width: 100%;
            height: 100%;
        }
        input {
            padding: 0 var(--padding-form-control);
            height: var(--height-form-control);
        }
    }

    // ----------------------- //
    // === Prefix & Suffix === //
    // ----------------------- //
    &:has(
            ${LabeledPrefix.toString()}:nth-child(1)
                + ${FormControl.toString()}:nth-child(2)
                + ${LabeledSuffix.toString()}:nth-child(3)
        ) {
        ${FormControl} {
            border-radius: 0;
        }
    }

    ${({disabled}) => {
        if (disabled)
            return css`
                --form-box-background-color: var(--container-background-color-disabled-100);
                --form-box-border-color: var(--container-border-color-100);
                --form-labeled-affix-background-color: var(--container-background-color-disabled-200);
                --form-labeled-affix-border-color: var(--container-border-color-100);
                --form-box-hover-background-color: var(--container-border-color-100);
                * {
                    user-select: none;
                    cursor: not-allowed;
                }
                ${FormControl} {
                    cursor: not-allowed;
                }
            `;
    }}

    ${formFocus()};
`;

export const FormWrapper = styled.label`
    width: 100%;
    margin: 0;
    padding: 0;
`;
